import { Box, SxProps, Theme } from '@mui/material';

import { type DictationControls, DictationToolbarButton } from './DictationToolbarButton';
import { useRichTextEditor } from './editor';
import { EditorToolbarButton } from './EditorToolbarButton';

type RichTextEditorToolbarProps = Readonly<{
  onOpenLinkDialog: () => void;
  onInsertImage?: () => void;
  onAttachFile?: () => void;
  attachmentSlotsRemaining?: number;
  dictation?: DictationControls;
}>;

const Divider = () => <Box sx={dividerSx} role="separator" />;

export const RichTextEditorToolbar = ({
  onOpenLinkDialog,
  onInsertImage,
  onAttachFile,
  attachmentSlotsRemaining,
  dictation,
}: RichTextEditorToolbarProps) => {
  const { commands, activeStates } = useRichTextEditor();
  const attachmentsAtCap =
    typeof attachmentSlotsRemaining === 'number' && attachmentSlotsRemaining <= 0;

  return (
    <Box sx={toolbarContainerSx} role="toolbar" aria-label="Formatting toolbar">
      <EditorToolbarButton
        id="rt-toolbar-bold"
        label="Bold"
        icon="Bold01"
        active={activeStates.bold}
        onClick={() => commands.toggleBold()}
      />
      <EditorToolbarButton
        id="rt-toolbar-italic"
        label="Italic"
        icon="Italic01"
        active={activeStates.italic}
        onClick={() => commands.toggleItalic()}
      />
      <EditorToolbarButton
        id="rt-toolbar-underline"
        label="Underline"
        icon="Underline01"
        active={activeStates.underline}
        onClick={() => commands.toggleUnderline()}
      />
      <Divider />
      <EditorToolbarButton
        id="rt-toolbar-bullet-list"
        label="Bullet list"
        icon="List"
        active={activeStates.unorderedList}
        onClick={() => commands.toggleUnorderedList()}
      />
      {activeStates.unorderedList && (
        <>
          <EditorToolbarButton
            id="rt-toolbar-indent"
            label="Indent"
            icon="LeftIndent01"
            onClick={() => commands.indentList()}
          />
          <EditorToolbarButton
            id="rt-toolbar-outdent"
            label="Outdent"
            icon="RightIndent01"
            onClick={() => commands.outdentList()}
          />
        </>
      )}
      <Divider />
      <EditorToolbarButton
        id="rt-toolbar-align-left"
        label="Align left"
        icon="AlignLeft"
        active={activeStates.isAlignedLeft}
        onClick={() => commands.alignLeft()}
      />
      <EditorToolbarButton
        id="rt-toolbar-align-center"
        label="Align center"
        icon="AlignCenter"
        active={activeStates.isAlignedCenter}
        onClick={() => commands.alignCenter()}
      />
      <Divider />
      <EditorToolbarButton
        id="rt-toolbar-link"
        label="Insert link"
        icon="Link01"
        active={activeStates.isLink}
        onClick={onOpenLinkDialog}
      />
      {(onInsertImage || onAttachFile || dictation) && <Divider />}
      {onInsertImage && (
        <EditorToolbarButton
          id="rt-toolbar-image"
          label={attachmentsAtCap ? 'Attachment limit reached' : 'Insert image'}
          icon="Image01"
          disabled={attachmentsAtCap}
          onClick={onInsertImage}
        />
      )}
      {onAttachFile && (
        <EditorToolbarButton
          id="rt-toolbar-attach"
          label={attachmentsAtCap ? 'Attachment limit reached' : 'Attach file'}
          icon="Paperclip"
          disabled={attachmentsAtCap}
          onClick={onAttachFile}
        />
      )}
      {dictation && <DictationToolbarButton {...dictation} />}
    </Box>
  );
};

const toolbarContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xxs'],
  marginBottom: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '1px',
  height: '20px',
  backgroundColor: backrs.colors['Gray/300'],
  marginX: backrs.spacing['spacing-xs'],
});
